import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionButton,
  ExtraInfo,
  FormattedDate,
  FormattedDateTime
} from '@elotech/components';
import { ArquivoEntity } from 'common/type';
import React from 'react';

import { tipoSigapEnum } from '../../type/enum';
import { isHtmlFile } from './arquivoUtils';

type Props = {
  file: ArquivoEntity;
  onRemoveFile(file: any): void;
  canDelete?: boolean;
  onUpdateFile(file: any): void;
  canUpdate?: boolean;
  sizeDivFile?: number;
  onDownload(file: any): void;
  onView(file: any): void;
  extraButton?: (file: ArquivoEntity) => any;
  labels?: (file: ArquivoEntity) => any;
  onChangeSigilo(file: ArquivoEntity): void;
  getExtraCaption?(file: ArquivoEntity): any;
  canEditHTML: boolean;
};

const DragDropItem: React.FunctionComponent<Props> = ({
  file,
  canDelete,
  canUpdate,
  sizeDivFile,
  onRemoveFile,
  onUpdateFile,
  onDownload,
  onView,
  extraButton,
  labels,
  onChangeSigilo,
  getExtraCaption,
  canEditHTML
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: file.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const isHtml = isHtmlFile(file.arquivo);

  const getUsuario = usuario => {
    return usuario ? <span> - Usuário: {usuario}</span> : '';
  };

  const getData = data => {
    return data ? (
      <>
        <span> - Data: </span>
        {data?.match(/^\d{4}-\d{2}-\d{2}$/) ? (
          <FormattedDate value={data} />
        ) : (
          <FormattedDateTime value={data} />
        )}
      </>
    ) : (
      ''
    );
  };

  const getTipoArquivo = (file: ArquivoEntity) => {
    if (file?.tipoArquivo?.descricao) {
      return <span> - Tipo de arquivo: {file?.tipoArquivo?.descricao}</span>;
    }

    return '';
  };

  const getTipoSigap = (file: ArquivoEntity) => {
    if (file?.tipoSigap) {
      return <span> - SIGAP: {tipoSigapEnum[file.tipoSigap]}</span>;
    }

    return '';
  };

  const resolveExtraCaptions = (file: any) => {
    const extraCaptions = getExtraCaption?.(file);

    if (!extraCaptions) {
      return '';
    }

    return (
      <>
        {Object.entries(extraCaptions).map(
          ([label, value]) =>
            !!value && (
              <span key={label}>
                {' - '}
                {label}
                {': '}
                {value}
              </span>
            )
        )}
      </>
    );
  };

  return (
    <li
      ref={setNodeRef}
      className={`col-md-${sizeDivFile}`}
      style={style}
      {...attributes}
    >
      <div className="dragdrop-item">
        <div style={{ display: 'flex' }}>
          {labels?.(file)}
          {file?.sigiloso && (
            <ExtraInfo
              letter={'S'}
              value={'Arquivo Sigiloso'}
              color={'warning'}
            />
          )}
        </div>
        <div className="name" {...listeners}>
          <span>{file.arquivo.nome}</span>
          {getUsuario(file.arquivo.usuarioResponsavel)}
          {getData(file.arquivo.dataCriacao || file.audit?.dataCriacao)}
          {getTipoArquivo(file)}
          {getTipoSigap(file)}
          {resolveExtraCaptions(file)}
        </div>
        <div className="buttons">
          {extraButton?.(file)}
          {file.sigiloso !== undefined && canUpdate && (
            <ActionButton
              icon="sign-out-alt"
              label={
                file.sigiloso
                  ? 'Remover sigilo do arquivo'
                  : 'Tornar arquivo sigiloso'
              }
              onClick={() => onChangeSigilo(file)}
            />
          )}
          <button
            type="button"
            className="btn circle"
            onClick={() => onView(file)}
          >
            {isHtml && canEditHTML ? (
              <>
                <i className="fa fa-pen-square" />
                <div className="btn-actions-label">
                  Visualizar/editar template
                </div>
              </>
            ) : (
              <>
                <i className="fa fa-eye" />
                <div className="btn-actions-label">Visualizar</div>
              </>
            )}
          </button>
          <button type="button" className="btn circle" onClick={onDownload}>
            <i className="fa fa-download" />
            <div className="btn-actions-label">Download</div>
          </button>
          {canDelete && (
            <button
              type="button"
              className="btn circle"
              onClick={onRemoveFile}
              id="btn-remove"
            >
              <i className="fa fa-trash" />
              <div className="btn-actions-label">Excluir</div>
            </button>
          )}
          {canUpdate && (
            <button
              type="button"
              className="btn circle"
              onClick={onUpdateFile}
              id="btn-update"
            >
              <i className="fa fa-pen" />
              <div className="btn-actions-label">Atualizar</div>
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

export default DragDropItem;
